import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DMS_BASE_COLOR_HEX, environment, SETTINGS_KEYS } from '../../config/environment'
import { useSetting } from '../../hooks/useSettings'
import { DATE_LOCALE_OPTIONS, DISPLAY_LANGUAGE_OPTIONS } from '../../i18n/languages'
import { ConfigCategory, ConfigOptionType, configService, DMSConfig, DMSConfigOptions } from '../../services/config.service'
import { documentTypeService } from '../../services/document-type.service'
import { groupService } from '../../services/group.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { userService } from '../../services/user.service'
import { useAppStore } from '../../store/useAppStore'
import { DocumentType, Group, SupportedUploadType, User } from '../../types'
import { formatDate } from '../../utils/dateFormatter'
import { useModuleFlag } from '../../utils/featureFlags'
import { DocumentationDialog } from '../common/DocumentationDialog'
import { IfPermissions } from '../common/IfPermissions'
import { SettingsActiveDirectorySection } from './SettingsActiveDirectorySection'
import { SettingsESignatureSection } from './SettingsESignatureSection'
import { InputColor } from '../common/InputColor'
import { InputPermissionsGroup } from '../common/InputPermissionsGroup'
import { InputPermissionsUser } from '../common/InputPermissionsUser'
import { InputSelect } from '../common/InputSelect'
import { InputSwitch } from '../common/InputSwitch'
import { InputTags } from '../common/InputTags'
import PageHeader from '../common/PageHeader'
import './Settings.scss'

interface SettingsFormData {
  displayLanguage: string
  dateLocale: string
  dateFormat: string
  documentListItemPerPage: number
  slimSidebarEnabled: boolean
  darkModeUseSystem: boolean
  darkModeEnabled: boolean
  darkModeInvertThumbs: boolean
  themeColor: string
  updateCheckingEnabled: boolean
  savedViewsWarnOnUnsavedChange: boolean
  sidebarViewsShowCount: boolean
  useNativePdfViewer: boolean
  pdfViewerDefaultZoom: string
  documentEditingRemoveInboxTags: boolean
  documentEditingOverlayThumbnail: boolean
  documentListShowVersionWatermark: boolean
  documentListShowStatusWatermark: boolean
  searchDbOnly: boolean
  searchLink: string
  bulkEditConfirmationDialogs: boolean
  bulkEditApplyOnClose: boolean
  notesEnabled: boolean
  defaultPermsOwner: number | null
  defaultPermsViewUsers: number[]
  defaultPermsViewGroups: number[]
  defaultPermsEditUsers: number[]
  defaultPermsEditGroups: number[]
  notificationsConsumerNewDocument: boolean
  notificationsConsumerSuccess: boolean
  notificationsConsumerFailed: boolean
  notificationsConsumerSuppressOnDashboard: boolean
  notificationsWorkflowExecuted: boolean
}

type SettingsSection =
  | 'appearance'
  | 'branding'
  | 'notifications'
  | 'saved-views'
  | 'global-search'
  | 'documents'
  | 'notes'
  | 'permissions'
  | 'ocr'
  | 'barcode'
  | 'eSignature'
  | 'sef'
  | 'eotpremnica'
  | 'active-directory'

interface UploadTypesCardProps {
  configLoading: boolean
  configSaving: boolean
  configFormData: Partial<DMSConfig>
  handleConfigInputChange: (key: string, value: any) => void
}

const UploadTypesCard: React.FC<UploadTypesCardProps> = ({
  configLoading,
  configSaving,
  configFormData,
  handleConfigInputChange,
}) => {
  const { t } = useTranslation()
  const uiSettings = useAppStore((s) => s.uiSettings)
  const supportedTypes: SupportedUploadType[] = uiSettings?.settings?.supported_upload_types ?? []

  const allowedMimes: string[] | null = (configFormData.allowed_upload_mime_types as string[] | null) ?? null
  const allSelected = allowedMimes === null || allowedMimes === undefined

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      handleConfigInputChange('allowed_upload_mime_types', null)
    } else {
      handleConfigInputChange(
        'allowed_upload_mime_types',
        supportedTypes.map((s) => s.mime_type),
      )
    }
  }

  const toggleMime = (mime: string, checked: boolean) => {
    const current = allowedMimes ?? supportedTypes.map((s) => s.mime_type)
    let next: string[]
    if (checked) {
      next = [...new Set([...current, mime])]
    } else {
      next = current.filter((m) => m !== mime)
    }
    const allMimes = supportedTypes.map((s) => s.mime_type)
    if (next.length === allMimes.length && allMimes.every((m) => next.includes(m))) {
      handleConfigInputChange('allowed_upload_mime_types', null)
    } else {
      handleConfigInputChange('allowed_upload_mime_types', next.length > 0 ? next : null)
    }
  }

  const isMimeChecked = (mime: string): boolean => {
    if (allSelected) return true
    return allowedMimes!.includes(mime)
  }

  return (
    <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
      <div className="settings-card mt-3">
        <div className="settings-card__body">
          <h5>{t('allowed_upload_types')}</h5>
          <p className="text-muted small">{t('allowed_upload_types_help')}</p>

          {configLoading ? (
            <div className="d-flex justify-content-center py-3">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">{t('loading')}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  id="upload-type-all"
                  className="form-check-input"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={configSaving}
                />
                <label className="form-check-label fw-semibold" htmlFor="upload-type-all">
                  {t('all_supported_upload_types')}
                </label>
              </div>

              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-1 ms-3">
                {supportedTypes.map((entry) => (
                  <div className="col" key={entry.mime_type}>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id={`upload-type-${entry.mime_type}`}
                        className="form-check-input"
                        checked={isMimeChecked(entry.mime_type)}
                        onChange={(e) => toggleMime(entry.mime_type, e.target.checked)}
                        disabled={configSaving}
                      />
                      <label className="form-check-label small" htmlFor={`upload-type-${entry.mime_type}`}>
                        {entry.extensions.join(', ')}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </IfPermissions>
  )
}

export const Settings: React.FC = () => {
  const { t } = useTranslation()
  const { section } = useParams<{ section?: string }>()
  const navigate = useNavigate()
  const selectPortalTarget = typeof document !== 'undefined' ? document.body : null
  const ALL_SECTIONS: SettingsSection[] = [
    'appearance',
    'branding',
    'notifications',
    'saved-views',
    'global-search',
    'documents',
    'notes',
    'permissions',
    'ocr',
    'barcode',
    'eSignature',
    'sef',
    'eotpremnica',
    'active-directory',
  ]

  const SECTION_PERMISSIONS: Partial<Record<SettingsSection, { action: PermissionAction; type: PermissionType }>> = {
    branding: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    notifications: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    'saved-views': { action: PermissionAction.Change, type: PermissionType.AppConfig },
    'global-search': { action: PermissionAction.Change, type: PermissionType.AppConfig },
    documents: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    notes: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    permissions: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    ocr: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    barcode: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    eSignature: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    sef: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    eotpremnica: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    'active-directory': { action: PermissionAction.Change, type: PermissionType.AppConfig },
  }

  const isExternalIntegrationsModuleEnabled = useModuleFlag('external_integrations', true)
  const isSefModuleEnabled = useModuleFlag('sef_integration', false)
  const isElectronicSignatureModuleEnabled = useModuleFlag('electronic_signature', false)
  const isEotpremnicaModuleEnabled = useModuleFlag('eotpremnice_integration', false)
  const moduleFlagsRef = useRef({
    external_integrations: isExternalIntegrationsModuleEnabled,
    sef: isSefModuleEnabled,
    eotpremnica: isEotpremnicaModuleEnabled,
  })

  const isSectionModuleEnabled = (section: SettingsSection): boolean => {
    if (section === 'sef') {
      return isSefModuleEnabled
    }
    if (section === 'eSignature') {
      return isElectronicSignatureModuleEnabled
    }
    if (section === 'eotpremnica') {
      return isEotpremnicaModuleEnabled
    }
    return true
  }

  const firstAllowedSection = useMemo<SettingsSection>(() => {
    for (const s of ALL_SECTIONS) {
      if (!isSectionModuleEnabled(s)) {
        continue
      }
      const req = SECTION_PERMISSIONS[s]
      if (!req || permissionsService.currentUserCan(req.action, req.type)) {
        return s
      }
    }
    return 'appearance'
  }, [isExternalIntegrationsModuleEnabled, isSefModuleEnabled, isElectronicSignatureModuleEnabled, isEotpremnicaModuleEnabled])

  const initialSection: SettingsSection = (() => {
    if (!section) return 'appearance'
    const candidate = section as SettingsSection
    if (!ALL_SECTIONS.includes(candidate)) {
      return 'appearance'
    }
    return isSectionModuleEnabled(candidate) ? candidate : firstAllowedSection
  })()

  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection)
  const [isDirty, setIsDirty] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle)
  const canViewUsers = permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)
  const canViewGroups = permissionsService.currentUserCan(PermissionAction.View, PermissionType.Group)

  const [users, setUsers] = useState<User[]>([])
  const [, setGroups] = useState<Group[]>([])

  const [config, setConfig] = useState<DMSConfig | null>(null)
  const [configFormData, setConfigFormData] = useState<Partial<DMSConfig>>({})
  const [configLoading, setConfigLoading] = useState(false)
  const [configSaving, setConfigSaving] = useState(false)
  const [configErrors, setConfigErrors] = useState<Record<string, string>>({})
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File | null>>({})
  const [fileInputKeys, setFileInputKeys] = useState<Record<string, number>>({})
  const [showDocsDialog, setShowDocsDialog] = useState(false)
  const [currentDocKey, setCurrentDocKey] = useState<string>('')
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([])
  const [sefTestMessage, setSefTestMessage] = useState<string | null>(null)
  const [sefTestVariant, setSefTestVariant] = useState<'success' | 'danger' | 'warning' | 'info'>('info')
  const [sefTestLoading, setSefTestLoading] = useState(false)
  const [eotpremnicaTestMessage, setEotpremnicaTestMessage] = useState<string | null>(null)
  const [eotpremnicaTestVariant, setEotpremnicaTestVariant] = useState<'success' | 'danger' | 'warning' | 'info'>('info')
  const [eotpremnicaTestLoading, setEotpremnicaTestLoading] = useState(false)
  const eotpremnicaTypeMappings = [
    { key: 'eotpremnica_document_type_despatch_incoming_external', labelKey: 'eotpremnica_despatch_incoming_external' },
    { key: 'eotpremnica_document_type_despatch_incoming_internal', labelKey: 'eotpremnica_despatch_incoming_internal' },
    { key: 'eotpremnica_document_type_despatch_outgoing_external', labelKey: 'eotpremnica_despatch_outgoing_external' },
    { key: 'eotpremnica_document_type_despatch_outgoing_internal', labelKey: 'eotpremnica_despatch_outgoing_internal' },
    { key: 'eotpremnica_document_type_receipt_incoming_external', labelKey: 'eotpremnica_receipt_incoming_external' },
    { key: 'eotpremnica_document_type_receipt_incoming_internal', labelKey: 'eotpremnica_receipt_incoming_internal' },
    { key: 'eotpremnica_document_type_receipt_outgoing_external', labelKey: 'eotpremnica_receipt_outgoing_external' },
    { key: 'eotpremnica_document_type_receipt_outgoing_internal', labelKey: 'eotpremnica_receipt_outgoing_internal' },
  ]
  const eotpremnicaDespatchMappings = eotpremnicaTypeMappings.filter((mapping) => mapping.key.includes('despatch'))
  const eotpremnicaReceiptMappings = eotpremnicaTypeMappings.filter((mapping) => mapping.key.includes('receipt'))
  const sefInvoiceTypeMappings = [
    { code: '380', labelKey: 'sef_invoice_type_380' },
    { code: '381', labelKey: 'sef_invoice_type_381' },
    { code: '383', labelKey: 'sef_invoice_type_383' },
    { code: '386', labelKey: 'sef_invoice_type_386' },
  ]

  useEffect(() => {
    document.title = `${t('settings')} - ${appTitle}`
  }, [t, appTitle])

  const moduleSyncInFlightRef = useRef(false)

  useEffect(() => {
    if (!config) return
    if (configSaving) return
    if (moduleSyncInFlightRef.current) return

    const updates: Partial<DMSConfig> = {}
    let shouldPersist = false

    const currentExternalEnabled = configFormData.external_integrations_enabled ?? config.external_integrations_enabled

    if (typeof currentExternalEnabled === 'boolean' && currentExternalEnabled !== isExternalIntegrationsModuleEnabled) {
      updates.external_integrations_enabled = isExternalIntegrationsModuleEnabled
      shouldPersist = true
    }

    if (!isSefModuleEnabled && configFormData.sef_enabled) {
      updates.sef_enabled = false
      shouldPersist = true
    } else if (isSefModuleEnabled && !moduleFlagsRef.current.sef && !configFormData.sef_enabled) {
      updates.sef_enabled = true
      shouldPersist = true
    }

    if (!isEotpremnicaModuleEnabled && configFormData.eotpremnica_enabled) {
      updates.eotpremnica_enabled = false
      shouldPersist = true
    } else if (isEotpremnicaModuleEnabled && !moduleFlagsRef.current.eotpremnica && !configFormData.eotpremnica_enabled) {
      updates.eotpremnica_enabled = true
      shouldPersist = true
    }

    if (shouldPersist) {
      const persistUpdates = async () => {
        try {
          moduleSyncInFlightRef.current = true
          const payload = {
            ...config,
            ...configFormData,
            ...updates,
          } as DMSConfig
          const updatedConfig = await configService.saveConfig(payload)
          setConfig(updatedConfig)
          setConfigFormData(updatedConfig)
        } catch (error) {
          toastService.error(t('config_save_error'))
        } finally {
          moduleSyncInFlightRef.current = false
        }
      }

      persistUpdates()
    } else {
      moduleSyncInFlightRef.current = false
    }

    moduleFlagsRef.current = {
      external_integrations: isExternalIntegrationsModuleEnabled,
      sef: isSefModuleEnabled,
      eotpremnica: isEotpremnicaModuleEnabled,
    }
  }, [
    config,
    configFormData.external_integrations_enabled,
    configFormData.sef_enabled,
    configFormData.eotpremnica_enabled,
    isExternalIntegrationsModuleEnabled,
    isSefModuleEnabled,
    isEotpremnicaModuleEnabled,
    configSaving,
    t,
  ])

  useEffect(() => {
    if (section) {
      const candidate = section as SettingsSection
      if (ALL_SECTIONS.includes(candidate) && isSectionModuleEnabled(candidate)) {
        setActiveSection(candidate)
      } else {
        setActiveSection(firstAllowedSection)
      }
    } else {
      setActiveSection(firstAllowedSection)
    }
  }, [section, isSefModuleEnabled, isElectronicSignatureModuleEnabled, isEotpremnicaModuleEnabled, firstAllowedSection])

  useEffect(() => {
    const req = SECTION_PERMISSIONS[activeSection]
    if (req && !permissionsService.currentUserCan(req.action, req.type)) {
      const fallback = firstAllowedSection
      if (fallback !== activeSection) {
        setActiveSection(fallback)
      }
      return
    }

    if (!isSectionModuleEnabled(activeSection)) {
      const fallback = firstAllowedSection
      if (fallback !== activeSection) {
        setActiveSection(fallback)
      }
    }
  }, [activeSection, firstAllowedSection, isSefModuleEnabled, isElectronicSignatureModuleEnabled, isEotpremnicaModuleEnabled])

  useEffect(() => {
    if (activeSection) {
      navigate(`/settings/${activeSection}`, { replace: true })
    }
  }, [activeSection, navigate])

  const displayLanguageOptions = DISPLAY_LANGUAGE_OPTIONS
  const dateLocaleOptions = DATE_LOCALE_OPTIONS

  const [formData, setFormData] = useState<SettingsFormData>({
    displayLanguage: settingsService.get(SETTINGS_KEYS.LANGUAGE) || '',
    dateLocale: settingsService.get(SETTINGS_KEYS.DATE_LOCALE) || '',
    dateFormat: settingsService.get(SETTINGS_KEYS.DATE_FORMAT) || 'shortDate',
    documentListItemPerPage: settingsService.get(SETTINGS_KEYS.DOCUMENT_LIST_SIZE) || 50,
    slimSidebarEnabled: settingsService.getBoolean(SETTINGS_KEYS.SLIM_SIDEBAR, false),
    darkModeUseSystem: settingsService.getBoolean(SETTINGS_KEYS.DARK_MODE_USE_SYSTEM, false),
    darkModeEnabled: settingsService.getBoolean(SETTINGS_KEYS.DARK_MODE_ENABLED, false),
    darkModeInvertThumbs: settingsService.getBoolean(SETTINGS_KEYS.DARK_MODE_THUMB_INVERTED, true),
    themeColor: settingsService.get(SETTINGS_KEYS.THEME_COLOR) || '',
    updateCheckingEnabled: settingsService.getBoolean(SETTINGS_KEYS.UPDATE_CHECKING_ENABLED, false),
    savedViewsWarnOnUnsavedChange: settingsService.getBoolean(SETTINGS_KEYS.SAVED_VIEWS_WARN_ON_UNSAVED_CHANGE, true),
    sidebarViewsShowCount: settingsService.getBoolean(SETTINGS_KEYS.SIDEBAR_VIEWS_SHOW_COUNT, true),
    useNativePdfViewer: settingsService.getBoolean(SETTINGS_KEYS.USE_NATIVE_PDF_VIEWER, false),
    pdfViewerDefaultZoom: settingsService.get(SETTINGS_KEYS.PDF_VIEWER_ZOOM_SETTING) || 'page-width',
    documentEditingRemoveInboxTags: settingsService.getBoolean(SETTINGS_KEYS.DOCUMENT_EDITING_REMOVE_INBOX_TAGS, false),
    documentEditingOverlayThumbnail: settingsService.getBoolean(SETTINGS_KEYS.DOCUMENT_EDITING_OVERLAY_THUMBNAIL, true),
    documentListShowVersionWatermark: settingsService.getBoolean(SETTINGS_KEYS.DOCUMENT_LIST_SHOW_VERSION_WATERMARK, true),
    documentListShowStatusWatermark: settingsService.getBoolean(SETTINGS_KEYS.DOCUMENT_LIST_SHOW_STATUS_WATERMARK, true),
    searchDbOnly: settingsService.getBoolean(SETTINGS_KEYS.SEARCH_DB_ONLY, false),
    searchLink: settingsService.get(SETTINGS_KEYS.SEARCH_FULL_TYPE) || 'title-content',
    bulkEditConfirmationDialogs: settingsService.getBoolean(SETTINGS_KEYS.BULK_EDIT_CONFIRMATION_DIALOGS, true),
    bulkEditApplyOnClose: settingsService.getBoolean(SETTINGS_KEYS.BULK_EDIT_APPLY_ON_CLOSE, false),
    notesEnabled: settingsService.getBoolean(SETTINGS_KEYS.NOTES_ENABLED, true),
    defaultPermsOwner: settingsService.get(SETTINGS_KEYS.DEFAULT_PERMS_OWNER) || null,
    defaultPermsViewUsers: settingsService.get(SETTINGS_KEYS.DEFAULT_PERMS_VIEW_USERS) || [],
    defaultPermsViewGroups: settingsService.get(SETTINGS_KEYS.DEFAULT_PERMS_VIEW_GROUPS) || [],
    defaultPermsEditUsers: settingsService.get(SETTINGS_KEYS.DEFAULT_PERMS_EDIT_USERS) || [],
    defaultPermsEditGroups: settingsService.get(SETTINGS_KEYS.DEFAULT_PERMS_EDIT_GROUPS) || [],
    notificationsConsumerNewDocument: settingsService.getBoolean(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_NEW_DOCUMENT, true),
    notificationsConsumerSuccess: settingsService.getBoolean(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUCCESS, true),
    notificationsConsumerFailed: settingsService.getBoolean(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_FAILED, true),
    notificationsConsumerSuppressOnDashboard: settingsService.getBoolean(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUPPRESS_ON_DASHBOARD, false),
    notificationsWorkflowExecuted: settingsService.getBoolean(SETTINGS_KEYS.NOTIFICATIONS_WORKFLOW_EXECUTED, true),
  })

  const [initialFormData, setInitialFormData] = useState<SettingsFormData>(formData)

  useEffect(() => {
    if (activeSection === 'permissions' || activeSection === 'documents') {
      if (canViewUsers) {
        loadUsers()
      }
      if (canViewGroups) {
        loadGroups()
      }
    }
    if (
      activeSection === 'branding' ||
      activeSection === 'ocr' ||
      activeSection === 'barcode' ||
      activeSection === 'documents' ||
      activeSection === 'notifications' ||
      activeSection === 'sef' ||
      activeSection === 'eotpremnica' ||
      activeSection === 'eSignature'
    ) {
      loadConfig()
    }
    if (activeSection === 'sef' || activeSection === 'eotpremnica') {
      loadDocumentTypes()
    }
  }, [activeSection, canViewUsers, canViewGroups])

  useEffect(() => {
    return () => {
      if (isDirty) {
        settingsService.updateAppearanceSettings()
      }
    }
  }, [isDirty])

  useEffect(() => {
    const dirty = JSON.stringify(formData) !== JSON.stringify(initialFormData)
    setIsDirty(dirty)
  }, [formData, initialFormData])

  useEffect(() => {
    settingsService.updateAppearanceSettings(formData.darkModeUseSystem, formData.darkModeEnabled, formData.themeColor, false)
  }, [formData.darkModeUseSystem, formData.darkModeEnabled, formData.themeColor])

  const loadUsers = async () => {
    if (!canViewUsers) return
    try {
      const response = await userService.listAll({ page_size: 100 })
      setUsers(response.results)
    } catch (error) {}
  }

  const loadGroups = async () => {
    if (!canViewGroups) return
    try {
      const response = await groupService.listAll({ page_size: 100 })
      setGroups(response.results)
    } catch (error) {}
  }

  const loadConfig = async () => {
    try {
      setConfigLoading(true)
      const configData = await configService.getConfig()
      setConfig(configData)
      setConfigFormData(configData)
      setSelectedFiles({})
      setFileInputKeys({})
      setSefTestMessage(null)
      setEotpremnicaTestMessage(null)
    } catch (error) {
      toastService.showError(t('error_loading_config'))
    } finally {
      setConfigLoading(false)
    }
  }

  const loadDocumentTypes = async () => {
    try {
      const response = await documentTypeService.listAll()
      setDocumentTypes(response.results)
    } catch (error) {}
  }

  const handleInputChange = (field: keyof SettingsFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleConfigInputChange = (key: string, value: any) => {
    setConfigFormData((prev) => ({
      ...prev,
      [key]: value,
    }))

    if (DMSConfigOptions.find((opt) => opt.key === key)?.type === ConfigOptionType.JSON) {
      if (value && value.toString().length > 0) {
        try {
          JSON.parse(value)
          setConfigErrors((prev) => ({ ...prev, [key]: '' }))
        } catch (e) {
          setConfigErrors((prev) => ({ ...prev, [key]: t('invalid_json') }))
        }
      } else {
        setConfigErrors((prev) => ({ ...prev, [key]: '' }))
      }
    }
  }

  const handleFileUpload = async (file: File, key: string) => {
    if (!config) return

    try {
      setConfigSaving(true)
      const updatedConfig = await configService.uploadFile(file, config.id, key)
      setConfig(updatedConfig)
      setConfigFormData(updatedConfig)
      setSelectedFiles((prev) => ({ ...prev, [key]: null }))
      setFileInputKeys((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }))
      await settingsService.initializeSettings(true)
      toastService.showSuccess(t('file_successfully_uploaded'))
    } catch (error: any) {
      let errorMessage = t('error_uploading_file')

      if (error?.response?.data) {
        const errorData = error.response.data

        if (errorData[key] && Array.isArray(errorData[key])) {
          errorMessage = errorData[key].join(' ')
        } else if (typeof errorData === 'object') {
          const firstError = Object.values(errorData).flat()[0]
          if (firstError) {
            errorMessage = Array.isArray(firstError) ? firstError.join(' ') : String(firstError)
          }
        } else if (typeof errorData === 'string') {
          errorMessage = errorData
        }
      }

      toastService.showError(errorMessage)
    } finally {
      setConfigSaving(false)
    }
  }

  const handleRemoveFile = async (key: string) => {
    if (!config) return

    try {
      setConfigSaving(true)
      const updatedFormData = { ...configFormData, [key]: null }
      const updatedConfig = await configService.saveConfig(updatedFormData as DMSConfig)
      setConfig(updatedConfig)
      setConfigFormData(updatedConfig)
      setSelectedFiles((prev) => ({ ...prev, [key]: null }))
      setFileInputKeys((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }))
      await settingsService.initializeSettings(true)
      toastService.showSuccess(t('file_removed_successfully'))
    } catch (error) {
      toastService.showError(t('error_removing_file'))
    } finally {
      setConfigSaving(false)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await settingsService.setLanguage(formData.displayLanguage)
      settingsService.set(SETTINGS_KEYS.DATE_LOCALE, formData.dateLocale)
      settingsService.set(SETTINGS_KEYS.DATE_FORMAT, formData.dateFormat)
      settingsService.set(SETTINGS_KEYS.DOCUMENT_LIST_SIZE, formData.documentListItemPerPage)
      settingsService.set(SETTINGS_KEYS.SLIM_SIDEBAR, formData.slimSidebarEnabled)
      settingsService.set(SETTINGS_KEYS.DARK_MODE_USE_SYSTEM, formData.darkModeUseSystem)
      settingsService.set(SETTINGS_KEYS.DARK_MODE_ENABLED, formData.darkModeEnabled)
      settingsService.set(SETTINGS_KEYS.DARK_MODE_THUMB_INVERTED, formData.darkModeInvertThumbs)
      settingsService.set(SETTINGS_KEYS.THEME_COLOR, formData.themeColor)
      settingsService.set(SETTINGS_KEYS.UPDATE_CHECKING_ENABLED, formData.updateCheckingEnabled)
      settingsService.set(SETTINGS_KEYS.SAVED_VIEWS_WARN_ON_UNSAVED_CHANGE, formData.savedViewsWarnOnUnsavedChange)
      settingsService.set(SETTINGS_KEYS.SIDEBAR_VIEWS_SHOW_COUNT, formData.sidebarViewsShowCount)
      settingsService.set(SETTINGS_KEYS.USE_NATIVE_PDF_VIEWER, formData.useNativePdfViewer)
      settingsService.set(SETTINGS_KEYS.PDF_VIEWER_ZOOM_SETTING, formData.pdfViewerDefaultZoom)
      settingsService.set(SETTINGS_KEYS.DOCUMENT_EDITING_REMOVE_INBOX_TAGS, formData.documentEditingRemoveInboxTags)
      settingsService.set(SETTINGS_KEYS.DOCUMENT_EDITING_OVERLAY_THUMBNAIL, formData.documentEditingOverlayThumbnail)
      settingsService.set(SETTINGS_KEYS.DOCUMENT_LIST_SHOW_VERSION_WATERMARK, formData.documentListShowVersionWatermark)
      settingsService.set(SETTINGS_KEYS.DOCUMENT_LIST_SHOW_STATUS_WATERMARK, formData.documentListShowStatusWatermark)
      settingsService.set(SETTINGS_KEYS.SEARCH_DB_ONLY, formData.searchDbOnly)
      settingsService.set(SETTINGS_KEYS.SEARCH_FULL_TYPE, formData.searchLink)
      settingsService.set(SETTINGS_KEYS.BULK_EDIT_CONFIRMATION_DIALOGS, formData.bulkEditConfirmationDialogs)
      settingsService.set(SETTINGS_KEYS.BULK_EDIT_APPLY_ON_CLOSE, formData.bulkEditApplyOnClose)
      settingsService.set(SETTINGS_KEYS.NOTES_ENABLED, formData.notesEnabled)
      settingsService.set(SETTINGS_KEYS.DEFAULT_PERMS_OWNER, formData.defaultPermsOwner)
      settingsService.set(SETTINGS_KEYS.DEFAULT_PERMS_VIEW_USERS, formData.defaultPermsViewUsers)
      settingsService.set(SETTINGS_KEYS.DEFAULT_PERMS_VIEW_GROUPS, formData.defaultPermsViewGroups)
      settingsService.set(SETTINGS_KEYS.DEFAULT_PERMS_EDIT_USERS, formData.defaultPermsEditUsers)
      settingsService.set(SETTINGS_KEYS.DEFAULT_PERMS_EDIT_GROUPS, formData.defaultPermsEditGroups)
      settingsService.set(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_NEW_DOCUMENT, formData.notificationsConsumerNewDocument)
      settingsService.set(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUCCESS, formData.notificationsConsumerSuccess)
      settingsService.set(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_FAILED, formData.notificationsConsumerFailed)
      settingsService.set(SETTINGS_KEYS.NOTIFICATIONS_CONSUMER_SUPPRESS_ON_DASHBOARD, formData.notificationsConsumerSuppressOnDashboard)
      settingsService.set(SETTINGS_KEYS.NOTIFICATIONS_WORKFLOW_EXECUTED, formData.notificationsWorkflowExecuted)

      let savedOrgConfigThisRun = false
      if ((activeSection === 'documents' || activeSection === 'notifications') && config && isConfigDirty()) {
        setConfigSaving(true)
        try {
          const updatedConfig = await configService.saveConfig(configFormData as DMSConfig)
          setConfig(updatedConfig)
          setConfigFormData(updatedConfig)
          savedOrgConfigThisRun = true
        } catch (error) {
          toastService.showError(t('error_saving_config'))
          setIsSaving(false)
          return
        } finally {
          setConfigSaving(false)
        }
      }

      await settingsService.storeSettings()

      await settingsService.updateAppearanceSettings(formData.darkModeUseSystem, formData.darkModeEnabled, formData.themeColor, true)

      if (savedOrgConfigThisRun) {
        await settingsService.initializeSettings(true)
      }

      setInitialFormData(formData)
      setIsDirty(false)

      toastService.showSuccess(t('settings_saved_successfully'))
    } catch (error) {
      toastService.showError(t('error_saving_settings'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleConfigSave = async (): Promise<boolean> => {
    if (!config) return false

    try {
      setConfigSaving(true)
      const updatedConfig = await configService.saveConfig(configFormData as DMSConfig)
      setConfig(updatedConfig)
      setConfigFormData(updatedConfig)
      await settingsService.initializeSettings(true)
      toastService.showSuccess(t('config_saved_successfully'))
      return true
    } catch (error) {
      toastService.showError(t('error_saving_config'))
      return false
    } finally {
      setConfigSaving(false)
    }
  }

  const handleReset = () => {
    setFormData(initialFormData)
    if ((activeSection === 'documents' || activeSection === 'notifications') && config) {
      setConfigFormData(config)
      setConfigErrors({})
    }
    settingsService.updateAppearanceSettings()
  }

  const handleConfigDiscard = () => {
    if (config) {
      setConfigFormData(config)
      setConfigErrors({})
      setSelectedFiles({})
      setFileInputKeys({})
      setSefTestMessage(null)
    }
  }

  const handleClearThemeColor = () => {
    handleInputChange('themeColor', DMS_BASE_COLOR_HEX)
  }

  const handleDocsClick = (key: string) => {
    setCurrentDocKey(key)
    setShowDocsDialog(true)
  }

  const isConfigDirty = () => {
    if (!config) return false
    return JSON.stringify(config) !== JSON.stringify(configFormData)
  }

  const isConfigFormValid = () => {
    return Object.values(configErrors).every((error) => !error)
  }

  const getCategoryOptions = (category: string) => {
    return DMSConfigOptions.filter((option) => option.category === category)
  }

  const renderConfigOption = (option: (typeof DMSConfigOptions)[0]) => {
    const visibilityByOptionKey: Record<string, { key: keyof DMSConfig }> = {
      app_logo: { key: 'show_app_logo' },
      app_logo_icon: { key: 'show_app_logo_icon' },
      app_title: { key: 'show_app_title' },
      app_subtitle: { key: 'show_app_subtitle' },
    }

    const visibility = visibilityByOptionKey[option.key]
    const optionTitle = (option as any).titleKey ? t((option as any).titleKey) : option.title

    return (
      <Card key={option.key} className="mb-3">
        <Card.Body>
          <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-1 flex-grow-1">
              <h6 className="mb-0">{optionTitle}</h6>
              <a
                className="btn btn-sm btn-link p-0"
                title={t('read_the_documentation_about_this_setting')}
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  handleDocsClick(option.config_key || option.key)
                }}
                style={{ cursor: 'pointer' }}
              >
                <i className="bi bi-info-circle"></i>
              </a>
            </div>

            {visibility && (
              <div className="d-flex align-items-center ms-auto" title={t('show')}>
                <Form.Check
                  type="switch"
                  id={`visibility-${option.key}`}
                  checked={Boolean((configFormData[visibility.key] as any) ?? true)}
                  onChange={(e) => handleConfigInputChange(String(visibility.key), e.target.checked)}
                  disabled={configSaving}
                />
                <Form.Label htmlFor={`visibility-${option.key}`} className="ms-2 mb-0 text-muted small">
                  {t('show')}
                </Form.Label>
              </div>
            )}
          </Card.Title>
          <div className="mb-n3">
            {option.type === ConfigOptionType.Select && (
              <Form.Group>
                <Form.Select
                  value={String(configFormData[option.key as keyof DMSConfig] || '')}
                  onChange={(e) => handleConfigInputChange(option.key, e.target.value)}
                  isInvalid={!!configErrors[option.key]}
                >
                  <option value="">{t('select_option')}</option>
                  {option.choices?.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </Form.Select>
                {configErrors[option.key] && <Form.Control.Feedback type="invalid">{configErrors[option.key]}</Form.Control.Feedback>}
              </Form.Group>
            )}

            {option.type === ConfigOptionType.Number && (
              <Form.Group>
                <Form.Control
                  type="number"
                  value={String(configFormData[option.key as keyof DMSConfig] || '')}
                  onChange={(e) => handleConfigInputChange(option.key, e.target.value ? Number(e.target.value) : null)}
                  isInvalid={!!configErrors[option.key]}
                  placeholder={t('enter_number')}
                />
                {configErrors[option.key] && <Form.Control.Feedback type="invalid">{configErrors[option.key]}</Form.Control.Feedback>}
              </Form.Group>
            )}

            {option.type === ConfigOptionType.Boolean && (
              <Form.Group>
                <InputSwitch
                  id={`switch-${option.key}`}
                  horizontal={true}
                  value={!!configFormData[option.key as keyof DMSConfig]}
                  onChange={(value) => handleConfigInputChange(option.key, value)}
                  title={configFormData[option.key as keyof DMSConfig] ? t('enabled') : t('disabled')}
                  disabled={configSaving}
                />
              </Form.Group>
            )}

            {option.type === ConfigOptionType.String && (
              <Form.Group>
                <Form.Control
                  type="text"
                  value={String(configFormData[option.key as keyof DMSConfig] || '')}
                  onChange={(e) => handleConfigInputChange(option.key, e.target.value)}
                  isInvalid={!!configErrors[option.key]}
                  placeholder={option.key === 'language' ? t('ocr_language_placeholder') : t('enter_text')}
                />
                {option.key === 'language' && <Form.Text className="text-muted">{t('ocr_language_hint')}</Form.Text>}
                {configErrors[option.key] && <Form.Control.Feedback type="invalid">{configErrors[option.key]}</Form.Control.Feedback>}
              </Form.Group>
            )}

            {option.type === ConfigOptionType.JSON && (
              <Form.Group>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={
                    configFormData[option.key as keyof DMSConfig] ? JSON.stringify(configFormData[option.key as keyof DMSConfig], null, 2) : '{}'
                  }
                  onChange={(e) => handleConfigInputChange(option.key, e.target.value)}
                  isInvalid={!!configErrors[option.key]}
                  placeholder="{}"
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    resize: 'vertical',
                  }}
                />
                {configErrors[option.key] && <Form.Control.Feedback type="invalid">{configErrors[option.key]}</Form.Control.Feedback>}
              </Form.Group>
            )}

            {option.type === ConfigOptionType.File && (
              <Form.Group>
                {(() => {
                  const selectedFile = selectedFiles[option.key] || null
                  const inputKey = fileInputKeys[option.key] || 0
                  return (
                    <>
                      <div className="d-flex flex-column gap-2">
                        <input
                          key={inputKey}
                          id={`file-input-${option.key}`}
                          type="file"
                          onChange={(e) => {
                            const target = e.target as HTMLInputElement
                            const file = target.files?.[0]
                            if (file) {
                              setSelectedFiles((prev) => ({ ...prev, [option.key]: file }))
                            }
                          }}
                          disabled={configSaving}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={selectedFile ? selectedFile.name : ''}
                          placeholder={t('no_file_chosen')}
                          readOnly
                          disabled={configSaving}
                        />
                        <div className="d-flex gap-2 justify-content-end">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => document.getElementById(`file-input-${option.key}`)?.click()}
                            disabled={configSaving}
                          >
                            {t('choose_file')}
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              const file = selectedFiles[option.key]
                              if (file) {
                                handleFileUpload(file, option.key)
                              }
                            }}
                            disabled={configSaving || !selectedFiles[option.key]}
                          >
                            {t('upload')}
                          </Button>
                        </div>
                      </div>
                      {configFormData[option.key as keyof DMSConfig] && (
                        <div className="form-text d-flex align-items-center justify-content-between mt-1">
                          <span className="text-muted text-truncate">
                            {String(configFormData[option.key as keyof DMSConfig])
                              .split('/')
                              .pop()}
                          </span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger ms-2 p-0"
                            onClick={() => handleRemoveFile(option.key)}
                            disabled={configSaving}
                          >
                            <i className="bi bi-x"></i>
                            <small>{t('remove')}</small>
                          </button>
                        </div>
                      )}
                    </>
                  )
                })()}
              </Form.Group>
            )}
          </div>
        </Card.Body>
      </Card>
    )
  }

  const today = new Date()

  const formatDateExample = (formatStr: string, localeOverride?: string): string => {
    try {
      const userLocale = localeOverride || formData.dateLocale || 'sr-cs'
      return formatDate(today.toISOString(), formatStr, userLocale)
    } catch (error) {
      return today.toLocaleDateString()
    }
  }

  const menuItems: Array<{ id: SettingsSection; label: string; icon: string; permission?: { action: PermissionAction; type: PermissionType } }> = [
    { id: 'appearance', label: t('appearance'), icon: 'palette' },
    {
      id: 'branding',
      label: t('branding'),
      icon: 'paint-bucket',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    { id: 'notifications', label: t('notifications'), icon: 'bell', permission: { action: PermissionAction.Change, type: PermissionType.AppConfig } },
    {
      id: 'saved-views',
      label: t('saved_views'),
      icon: 'window-stack',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    {
      id: 'global-search',
      label: t('global_search'),
      icon: 'search',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    {
      id: 'documents',
      label: t('documents'),
      icon: 'file-earmark-text',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    { id: 'notes', label: t('notes'), icon: 'sticky', permission: { action: PermissionAction.Change, type: PermissionType.AppConfig } },
    { id: 'permissions', label: t('permissions'), icon: 'key', permission: { action: PermissionAction.Change, type: PermissionType.AppConfig } },
    {
      id: 'ocr',
      label: t('ocr_settings'),
      icon: 'file-text',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    {
      id: 'eSignature',
      label: t('eSignature_settings'),
      icon: 'shield-check',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    {
      id: 'sef',
      label: t('sef_settings'),
      icon: 'cloud-check',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    {
      id: 'eotpremnica',
      label: t('eotpremnica_settings'),
      icon: 'truck',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    {
      id: 'barcode',
      label: t('barcode_settings'),
      icon: 'upc-scan',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
    {
      id: 'active-directory',
      label: t('ad.menu_label'),
      icon: 'diagram-3',
      permission: { action: PermissionAction.Change, type: PermissionType.AppConfig },
    },
  ]

  const renderContent = () => {
    switch (activeSection) {
      case 'appearance':
        return (
          <div className="settings-card">
            <div className="settings-card__body">
              <h5>{t('appearance')}</h5>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('display_language')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <select
                    className="form-select"
                    value={formData.displayLanguage}
                    onChange={(e) => handleInputChange('displayLanguage', e.target.value)}
                  >
                    {displayLanguageOptions.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                        {lang.englishName && lang.code && ' - ' + lang.englishName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('date_display')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <select className="form-select" value={formData.dateLocale} onChange={(e) => handleInputChange('dateLocale', e.target.value)}>
                    {dateLocaleOptions.map((lang) => {
                      const exampleDate = lang.code ? formatDateExample('shortDate', lang.code) : ''
                      return (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                          {lang.code && exampleDate && ` - ${exampleDate}`}
                        </option>
                      )
                    })}
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('date_format')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <div className="form-check">
                    <input
                      type="radio"
                      id="dateFormatShort"
                      name="dateFormat"
                      className="form-check-input"
                      checked={formData.dateFormat === 'shortDate'}
                      onChange={() => handleInputChange('dateFormat', 'shortDate')}
                    />
                    <label className="form-check-label" htmlFor="dateFormatShort">
                      {t('short_date_example')}: {formatDateExample('shortDate')}
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      id="dateFormatMedium"
                      name="dateFormat"
                      className="form-check-input"
                      checked={formData.dateFormat === 'mediumDate'}
                      onChange={() => handleInputChange('dateFormat', 'mediumDate')}
                    />
                    <label className="form-check-label" htmlFor="dateFormatMedium">
                      {t('medium_date_example')}: {formatDateExample('mediumDate')}
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      id="dateFormatLong"
                      name="dateFormat"
                      className="form-check-input"
                      checked={formData.dateFormat === 'longDate'}
                      onChange={() => handleInputChange('dateFormat', 'longDate')}
                    />
                    <label className="form-check-label" htmlFor="dateFormatLong">
                      {t('long_date_example')}: {formatDateExample('longDate')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('items_per_page')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <select
                    className="form-select"
                    value={formData.documentListItemPerPage}
                    onChange={(e) => handleInputChange('documentListItemPerPage', Number(e.target.value))}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('sidebar')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="slimSidebar"
                      className="form-check-input"
                      checked={formData.slimSidebarEnabled}
                      onChange={(e) => handleInputChange('slimSidebarEnabled', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="slimSidebar">
                      {t('use_slim_sidebar')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('dark_mode')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="darkModeUseSystem"
                      className="form-check-input"
                      checked={formData.darkModeUseSystem}
                      onChange={(e) => handleInputChange('darkModeUseSystem', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="darkModeUseSystem">
                      {t('use_system_settings')}
                    </label>
                  </div>
                  {!formData.darkModeUseSystem && (
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="darkModeEnabled"
                        className="form-check-input"
                        checked={formData.darkModeEnabled}
                        onChange={(e) => handleInputChange('darkModeEnabled', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="darkModeEnabled">
                        {t('enable_dark_mode')}
                      </label>
                    </div>
                  )}
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="darkModeInvertThumbs"
                      className="form-check-input"
                      checked={formData.darkModeInvertThumbs}
                      onChange={(e) => handleInputChange('darkModeInvertThumbs', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="darkModeInvertThumbs">
                      {t('invert_thumbnails_in_dark_mode')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('theme_color')}</span>
                </div>
                <div className="col-md-6 col-lg-8">
                  <InputColor value={formData.themeColor || DMS_BASE_COLOR_HEX} onChange={(value) => handleInputChange('themeColor', value)} />
                </div>
                <div className="col-md-3 col-lg-2">
                  <button className="btn btn-link btn-sm pt-2 ps-0" disabled={!formData.themeColor} onClick={handleClearThemeColor}>
                    <i className="bi bi-x"></i> {t('reset')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )

      case 'saved-views':
        return (
          <div className="settings-card">
            <div className="settings-card__body">
              <h5>{t('saved_views')}</h5>
              <div className="row mb-3">
                <div className="col">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="savedViewsWarn"
                      className="form-check-input"
                      checked={formData.savedViewsWarnOnUnsavedChange}
                      onChange={(e) => handleInputChange('savedViewsWarnOnUnsavedChange', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="savedViewsWarn">
                      {t('show_warning_on_close_with_unsaved_changes')}
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="sidebarViewsCount"
                      className="form-check-input"
                      checked={formData.sidebarViewsShowCount}
                      onChange={(e) => handleInputChange('sidebarViewsShowCount', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="sidebarViewsCount">
                      {t('show_document_count_in_sidebar')}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'documents':
        return (
          <>
            <div className="settings-card">
              <div className="settings-card__body">
                <h5>{t('document_editing')}</h5>

                <div className="row mb-3">
                  <div className="col">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="useNativePdfViewer"
                        className="form-check-input"
                        checked={formData.useNativePdfViewer}
                        onChange={(e) => handleInputChange('useNativePdfViewer', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="useNativePdfViewer">
                        {t('use_native_pdf_viewer')}
                      </label>
                    </div>
                    <small className="form-text text-muted">{t('native_pdf_viewer_description')}</small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-3 col-form-label pt-0">
                    <span>{t('default_zoom')}</span>
                  </div>
                  <div className="col">
                    <select
                      className="form-select"
                      value={formData.pdfViewerDefaultZoom}
                      onChange={(e) => handleInputChange('pdfViewerDefaultZoom', e.target.value)}
                    >
                      <option value="page-width">{t('fit_to_width')}</option>
                      <option value="page-fit">{t('fit_to_page')}</option>
                    </select>
                    <small className="form-text text-muted">{t('pdf_viewer_zoom_description')}</small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="removeInboxTags"
                        className="form-check-input"
                        checked={formData.documentEditingRemoveInboxTags}
                        onChange={(e) => handleInputChange('documentEditingRemoveInboxTags', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="removeInboxTags">
                        {t('automatically_remove_inbox_tags_on_save')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="overlayThumbnail"
                        className="form-check-input"
                        checked={formData.documentEditingOverlayThumbnail}
                        onChange={(e) => handleInputChange('documentEditingOverlayThumbnail', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="overlayThumbnail">
                        {t('show_thumbnail_during_loading')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="showVersionWatermark"
                        className="form-check-input"
                        checked={formData.documentListShowVersionWatermark}
                        onChange={(e) => handleInputChange('documentListShowVersionWatermark', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="showVersionWatermark">
                        {t('show_document_version_watermark_on_cards')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="showStatusWatermark"
                        className="form-check-input"
                        checked={formData.documentListShowStatusWatermark}
                        onChange={(e) => handleInputChange('documentListShowStatusWatermark', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="showStatusWatermark">
                        {t('show_document_status_watermark_on_cards')}
                      </label>
                    </div>
                  </div>
                </div>

                <h5 className="mt-4">{t('bulk_editing')}</h5>
                <div className="row mb-3">
                  <div className="col">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="bulkEditConfirm"
                        className="form-check-input"
                        checked={formData.bulkEditConfirmationDialogs}
                        onChange={(e) => handleInputChange('bulkEditConfirmationDialogs', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="bulkEditConfirm">
                        {t('show_confirmation_dialogs')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="bulkEditApplyOnClose"
                        className="form-check-input"
                        checked={formData.bulkEditApplyOnClose}
                        onChange={(e) => handleInputChange('bulkEditApplyOnClose', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="bulkEditApplyOnClose">
                        {t('apply_on_close')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <UploadTypesCard
              configLoading={configLoading}
              configSaving={configSaving}
              configFormData={configFormData}
              handleConfigInputChange={handleConfigInputChange}
            />
          </>
        )

      case 'global-search':
        return (
          <div className="settings-card">
            <div className="settings-card__body">
              <h5>{t('global_search')}</h5>
              <div className="row mb-3">
                <div className="col">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="searchDbOnly"
                      className="form-check-input"
                      checked={formData.searchDbOnly}
                      onChange={(e) => handleInputChange('searchDbOnly', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="searchDbOnly">
                      {t('search_db_only')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-form-label pt-0">
                  <span>{t('full_search_leads_to')}</span>
                </div>
                <div className="col">
                  <select className="form-select" value={formData.searchLink} onChange={(e) => handleInputChange('searchLink', e.target.value)}>
                    <option value="title-content">{t('search_titles_and_content')}</option>
                    <option value="advanced">{t('advanced_search')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )

      case 'notes':
        return (
          <div className="settings-card">
            <div className="settings-card__body">
              <h5>{t('notes')}</h5>
              <div className="row mb-3">
                <div className="col">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="notesEnabled"
                      className="form-check-input"
                      checked={formData.notesEnabled}
                      onChange={(e) => handleInputChange('notesEnabled', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="notesEnabled">
                      {t('enable_notes')}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'permissions':
        return (
          <div className="settings-card">
            <div className="settings-card__body">
              <h5>{t('default_permissions')}</h5>
              <div className="row mb-3">
                <div className="col">
                  <p className="text-muted">{t('settings_apply_to_this_user_account_for_objects_ta')}</p>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('default_owner')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <select
                    className="form-select"
                    value={formData.defaultPermsOwner || ''}
                    onChange={(e) => handleInputChange('defaultPermsOwner', e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">{t('no_owner')}</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted text-end d-block">{t('objects_without_owner_can_be_viewed_by_all')}</small>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('default_view_permissions')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <div className="row mb-2">
                    <div className="col-2">
                      <span className="d-block pt-1">{t('users')}:</span>
                    </div>
                    <div className="col-10">
                      <InputPermissionsUser
                        value={formData.defaultPermsViewUsers}
                        onChange={(value) => handleInputChange('defaultPermsViewUsers', value)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-2">
                      <span className="d-block pt-1">{t('groups')}:</span>
                    </div>
                    <div className="col-10">
                      <InputPermissionsGroup
                        value={formData.defaultPermsViewGroups}
                        onChange={(value) => handleInputChange('defaultPermsViewGroups', value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-3 col-lg-2 col-form-label pt-0">
                  <span>{t('default_edit_permissions')}</span>
                </div>
                <div className="col-md-9 col-lg-10">
                  <div className="row mb-2">
                    <div className="col-2">
                      <span className="d-block pt-1">{t('users')}:</span>
                    </div>
                    <div className="col-10">
                      <InputPermissionsUser
                        value={formData.defaultPermsEditUsers}
                        onChange={(value) => handleInputChange('defaultPermsEditUsers', value)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-2">
                      <span className="d-block pt-1">{t('groups')}:</span>
                    </div>
                    <div className="col-10">
                      <InputPermissionsGroup
                        value={formData.defaultPermsEditGroups}
                        onChange={(value) => handleInputChange('defaultPermsEditGroups', value)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <small className="form-text text-muted text-end d-block">{t('edit_permissions_also_grant_view')}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'notifications':
        return (
          <>
            <div className="settings-card">
              <div className="settings-card__body">
                <h5>{t('document_processing')}</h5>
                <div className="row">
                  <div className="col">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="notifNewDoc"
                        className="form-check-input"
                        checked={formData.notificationsConsumerNewDocument}
                        onChange={(e) => handleInputChange('notificationsConsumerNewDocument', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="notifNewDoc">
                        {t('show_notifications_when_new_documents_detected')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="notifSuccess"
                        className="form-check-input"
                        checked={formData.notificationsConsumerSuccess}
                        onChange={(e) => handleInputChange('notificationsConsumerSuccess', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="notifSuccess">
                        {t('show_notifications_when_document_processing_successfully_finished')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="notifFailed"
                        className="form-check-input"
                        checked={formData.notificationsConsumerFailed}
                        onChange={(e) => handleInputChange('notificationsConsumerFailed', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="notifFailed">
                        {t('show_notifications_when_document_processing_failed')}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="notifSuppress"
                        className="form-check-input"
                        checked={formData.notificationsConsumerSuppressOnDashboard}
                        onChange={(e) => handleInputChange('notificationsConsumerSuppressOnDashboard', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="notifSuppress">
                        {t('suppress_notifications_on_dashboard')}
                      </label>
                    </div>
                    <small className="form-text text-muted d-block mt-2">{t('suppress_notifications_on_dashboard_description')}</small>
                    <h5 className="mt-4">{t('workflow_process_notifications')}</h5>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id="notifWorkflowExecuted"
                        className="form-check-input"
                        checked={formData.notificationsWorkflowExecuted}
                        onChange={(e) => handleInputChange('notificationsWorkflowExecuted', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="notifWorkflowExecuted">
                        {t('show_notifications_when_workflow_process_executed')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
              <div className="settings-card mt-3">
                <div className="settings-card__body">
                  <h5>{t('workflow_assignment_notifications')}</h5>
                  <p className="text-muted small">{t('workflow_assignment_notifications_help')}</p>
                  <Form.Group className="mb-0">
                    <Form.Label>{t('workflow_notification_delivery')}</Form.Label>
                    <Form.Select
                      value={configFormData.workflow_notification_delivery ?? 'websocket_only'}
                      onChange={(e) =>
                        handleConfigInputChange('workflow_notification_delivery', e.target.value as 'websocket_only' | 'websocket_and_email')
                      }
                      disabled={configSaving || configLoading}
                    >
                      <option value="websocket_only">{t('workflow_notification_delivery_websocket_only')}</option>
                      <option value="websocket_and_email">{t('workflow_notification_delivery_websocket_and_email')}</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </IfPermissions>
          </>
        )

      case 'branding':
        return (
          <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
            {configLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">{t('loading')}</span>
                </div>
              </div>
            ) : !config ? (
              <Alert variant="danger">{t('error_loading_config')}</Alert>
            ) : (
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleConfigSave()
                }}
              >
                <div className="settings-card">
                  <div className="settings-card__body">
                    <Row className="row-cols-1 row-cols-md-2 g-3">
                      <Col>{renderConfigOption(DMSConfigOptions.find((opt) => opt.key === 'app_logo')!)}</Col>
                      <Col>{renderConfigOption(DMSConfigOptions.find((opt) => opt.key === 'app_logo_icon')!)}</Col>
                      <Col>{renderConfigOption(DMSConfigOptions.find((opt) => opt.key === 'app_title')!)}</Col>
                      <Col>{renderConfigOption(DMSConfigOptions.find((opt) => opt.key === 'app_subtitle')!)}</Col>
                    </Row>
                  </div>
                </div>
                <div className="d-flex gap-2 mb-3 mt-3">
                  <Button type="button" variant="outline-secondary" onClick={handleConfigDiscard} disabled={configSaving || !isConfigDirty()}>
                    {t('discard')}
                  </Button>
                  <Button type="submit" variant="primary" disabled={configSaving || !isConfigFormValid() || !isConfigDirty()}>
                    {configSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('saving')}
                      </>
                    ) : (
                      t('save')
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </IfPermissions>
        )

      case 'ocr':
        return (
          <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
            {configLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">{t('loading')}</span>
                </div>
              </div>
            ) : !config ? (
              <Alert variant="danger">{t('error_loading_config')}</Alert>
            ) : (
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleConfigSave()
                }}
              >
                <div className="settings-card">
                  <div className="settings-card__body">
                    <Row className="row-cols-1 row-cols-md-2 row-cols-lg-3 g-2">
                      {getCategoryOptions(ConfigCategory.OCR).map((option) => (
                        <Col key={option.key}>{renderConfigOption(option)}</Col>
                      ))}
                    </Row>
                  </div>
                </div>
                <div className="d-flex gap-2 mb-3 mt-3">
                  <Button type="button" variant="outline-secondary" onClick={handleConfigDiscard} disabled={configSaving || !isConfigDirty()}>
                    {t('discard')}
                  </Button>
                  <Button type="submit" variant="primary" disabled={configSaving || !isConfigFormValid() || !isConfigDirty()}>
                    {configSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('saving')}
                      </>
                    ) : (
                      t('save')
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </IfPermissions>
        )

      case 'barcode':
        return (
          <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
            {configLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">{t('loading')}</span>
                </div>
              </div>
            ) : !config ? (
              <Alert variant="danger">{t('error_loading_config')}</Alert>
            ) : (
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleConfigSave()
                }}
              >
                <div className="settings-card">
                  <div className="settings-card__body">
                    <Row className="row-cols-1 row-cols-md-2 row-cols-lg-3 g-2">
                      {getCategoryOptions(ConfigCategory.Barcode).map((option) => (
                        <Col key={option.key}>{renderConfigOption(option)}</Col>
                      ))}
                    </Row>
                  </div>
                </div>
                <div className="d-flex gap-2 mb-3 mt-3">
                  <Button type="button" variant="outline-secondary" onClick={handleConfigDiscard} disabled={configSaving || !isConfigDirty()}>
                    {t('discard')}
                  </Button>
                  <Button type="submit" variant="primary" disabled={configSaving || !isConfigFormValid() || !isConfigDirty()}>
                    {configSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('saving')}
                      </>
                    ) : (
                      t('save')
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </IfPermissions>
        )

      case 'sef': {
        const sefControlsDisabled = !isSefModuleEnabled || configSaving

        return (
          <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
            {configLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">{t('loading')}</span>
                </div>
              </div>
            ) : !config ? (
              <Alert variant="danger">{t('error_loading_config')}</Alert>
            ) : (
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (isSefModuleEnabled && !configSaving) {
                    handleConfigSave()
                  }
                }}
              >
                <div className="settings-card">
                  <div className="settings-card__body">
                    {!isSefModuleEnabled && (
                      <Alert variant="warning" className="mb-3">
                        {t('sef_module_disabled_message', {
                          defaultValue: 'SEF integration is not available in your current package.',
                        })}
                      </Alert>
                    )}
                    <Row className="row-cols-1 row-cols-md-2 row-cols-lg-3 g-2">
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('sef_integration_enabled')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group>
                                <InputSwitch
                                  id="switch-sef-enabled"
                                  horizontal={true}
                                  value={!!configFormData.sef_enabled}
                                  onChange={(value) => handleConfigInputChange('sef_enabled', value)}
                                  title={configFormData.sef_enabled ? t('enabled') : t('disabled')}
                                  disabled={sefControlsDisabled}
                                />
                              </Form.Group>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('sef_url')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group>
                                <Form.Select
                                  value={String(configFormData.sef_url ?? '')}
                                  onChange={(e) => handleConfigInputChange('sef_url', e.target.value || null)}
                                  disabled={sefControlsDisabled}
                                >
                                  <option value="">{t('select_option')}</option>
                                  <option value="https://demoefaktura.mfin.gov.rs/">{t('sef_env_demo')}</option>
                                  <option value="https://efaktura.mfin.gov.rs/">{t('sef_env_prod')}</option>
                                </Form.Select>
                              </Form.Group>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('sef_fetch_interval_seconds')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group>
                                <Form.Control
                                  type="number"
                                  min={1}
                                  value={String(configFormData.sef_fetch_interval_seconds ?? 120)}
                                  onChange={(e) =>
                                    handleConfigInputChange('sef_fetch_interval_seconds', e.target.value ? Number(e.target.value) : 120)
                                  }
                                  placeholder={t('enter_number')}
                                  disabled={sefControlsDisabled}
                                />
                              </Form.Group>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('sef_max_document_age_days')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group>
                                <Form.Control
                                  type="number"
                                  min={1}
                                  value={String(configFormData.sef_max_document_age_days ?? '')}
                                  onChange={(e) =>
                                    handleConfigInputChange('sef_max_document_age_days', e.target.value ? Number(e.target.value) : null)
                                  }
                                  placeholder={t('enter_number')}
                                  disabled={sefControlsDisabled}
                                />
                              </Form.Group>
                              <Form.Text className="text-muted">{t('sef_max_document_age_help')}</Form.Text>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('sef_api_key')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group className="mb-2">
                                <Form.Control
                                  type="password"
                                  value={String(configFormData.sef_api_key || '')}
                                  onChange={(e) => handleConfigInputChange('sef_api_key', e.target.value)}
                                  placeholder={t('enter_text')}
                                  disabled={sefControlsDisabled}
                                />
                              </Form.Group>
                              <Form.Text className="text-muted">
                                {configFormData.sef_api_key_set ? t('api_key_configured') : t('api_key_not_configured')}
                              </Form.Text>
                              <div className="d-flex">
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  className="w-100"
                                  disabled={sefControlsDisabled || sefTestLoading}
                                  onClick={async () => {
                                    if (!isSefModuleEnabled) {
                                      return
                                    }

                                    const sefUrl = configFormData.sef_url
                                    setSefTestMessage(null)

                                    if (!sefUrl) {
                                      setSefTestVariant('danger')
                                      setSefTestMessage(t('sef_url_required'))
                                      return
                                    }
                                    // Ključ mora postojati: ili već sačuvan na serveru,
                                    // ili novouneti u formi (koji ćemo sačuvati pre testa).
                                    if (!configFormData.sef_api_key_set && !configFormData.sef_api_key) {
                                      setSefTestVariant('danger')
                                      setSefTestMessage(t('api_key_not_configured'))
                                      return
                                    }

                                    try {
                                      setSefTestLoading(true)
                                      // Test koristi ključ iz sačuvane konfiguracije, pa prvo
                                      // snimamo eventualne izmene (URL/ključ) da testiramo aktuelno.
                                      if (isConfigDirty()) {
                                        const saved = await handleConfigSave()
                                        if (!saved) {
                                          setSefTestVariant('danger')
                                          setSefTestMessage(t('error_saving_config'))
                                          return
                                        }
                                      }
                                      const result = await configService.testSefConnection({
                                        sef_url: sefUrl,
                                      })
                                      const message = result?.version
                                        ? t('sef_connection_success_version', { version: result.version })
                                        : t('sef_connection_success')
                                      setSefTestVariant('success')
                                      setSefTestMessage(message)
                                    } catch (error: any) {
                                      const backendMessage = error?.response?.data?.message
                                      setSefTestVariant('danger')
                                      setSefTestMessage(backendMessage || t('sef_connection_failed'))
                                    } finally {
                                      setSefTestLoading(false)
                                    }
                                  }}
                                >
                                  {sefTestLoading ? t('testing_connection') : t('sef_test_connection')}
                                </Button>
                              </div>
                              {sefTestMessage && (
                                <Alert variant={sefTestVariant} className="mt-2 mb-0">
                                  {sefTestMessage}
                                </Alert>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('sef_tags')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <InputTags
                                value={configFormData.sef_tag_ids || []}
                                onChange={(value) => handleConfigInputChange('sef_tag_ids', value)}
                                disabled={sefControlsDisabled}
                                menuPortalTarget={selectPortalTarget}
                              />
                              <Form.Text className="text-muted">{t('mail_rule_tags_help')}</Form.Text>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Card className="mb-12">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('sef_document_type_mapping')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-2">
                              <Table responsive borderless size="sm" className="mb-0" style={{ tableLayout: 'fixed' }}>
                                <colgroup>
                                  <col style={{ width: '10%' }} />
                                  <col style={{ width: '30%' }} />
                                  <col style={{ width: '30%' }} />
                                  <col style={{ width: '30%' }} />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th className="text-center align-middle">{t('sef_invoice_type_code')}</th>
                                    <th className="text-center align-middle">{t('sef_invoice_type_description')}</th>
                                    <th className="text-center align-middle">{t('sef_incoming')}</th>
                                    <th className="text-center align-middle">{t('sef_outgoing')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sefInvoiceTypeMappings.map((mapping) => (
                                    <tr key={mapping.code}>
                                      <td className="text-nowrap text-center align-middle">{mapping.code}</td>
                                      <td className="text-center align-middle">{t(mapping.labelKey)}</td>
                                      <td style={{ minWidth: 220 }} className="text-center align-middle">
                                        <div className="d-flex justify-content-center w-100">
                                          <div className="w-100">
                                            <InputSelect
                                              value={(configFormData as any)[`sef_document_type_purchase_${mapping.code}`] || null}
                                              onChange={(value) =>
                                                handleConfigInputChange(`sef_document_type_purchase_${mapping.code}`, value || null)
                                              }
                                              items={documentTypes}
                                              bindLabel="name"
                                              allowNull={true}
                                              placeholder={t('select_option')}
                                              disabled={sefControlsDisabled}
                                              menuPortalTarget={selectPortalTarget}
                                            />
                                          </div>
                                        </div>
                                      </td>
                                      <td style={{ minWidth: 220 }} className="text-center align-middle">
                                        <div className="d-flex justify-content-center w-100">
                                          <div className="w-100">
                                            <InputSelect
                                              value={(configFormData as any)[`sef_document_type_sales_${mapping.code}`] || null}
                                              onChange={(value) => handleConfigInputChange(`sef_document_type_sales_${mapping.code}`, value || null)}
                                              items={documentTypes}
                                              bindLabel="name"
                                              allowNull={true}
                                              placeholder={t('select_option')}
                                              disabled={sefControlsDisabled}
                                              menuPortalTarget={selectPortalTarget}
                                            />
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                              <Form.Text className="text-muted">{t('mail_rule_document_type_help')}</Form.Text>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </div>
                <div className="d-flex gap-2 mb-3 mt-3">
                  <Button type="button" variant="outline-secondary" onClick={handleConfigDiscard} disabled={sefControlsDisabled || !isConfigDirty()}>
                    {t('discard')}
                  </Button>
                  <Button type="submit" variant="primary" disabled={!isSefModuleEnabled || configSaving || !isConfigFormValid() || !isConfigDirty()}>
                    {configSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('saving')}
                      </>
                    ) : (
                      t('save')
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </IfPermissions>
        )
      }

      case 'eotpremnica': {
        const eotpremnicaControlsDisabled = !isEotpremnicaModuleEnabled || configSaving

        return (
          <IfPermissions action={PermissionAction.Change} type={PermissionType.AppConfig}>
            {configLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">{t('loading')}</span>
                </div>
              </div>
            ) : !config ? (
              <Alert variant="danger">{t('error_loading_config')}</Alert>
            ) : (
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (isEotpremnicaModuleEnabled && !configSaving) {
                    handleConfigSave()
                  }
                }}
              >
                <div className="settings-card">
                  <div className="settings-card__body">
                    {!isEotpremnicaModuleEnabled && (
                      <Alert variant="warning" className="mb-3">
                        {t('eotpremnica_module_disabled_message', {
                          defaultValue: 'E-otpremnica integration is not available in your current package.',
                        })}
                      </Alert>
                    )}
                    <Row className="row-cols-1 row-cols-md-2 row-cols-lg-3 g-2">
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('eotpremnica_integration_enabled')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group>
                                <InputSwitch
                                  id="switch-eotpremnica-enabled"
                                  horizontal={true}
                                  value={!!configFormData.eotpremnica_enabled}
                                  onChange={(value) => handleConfigInputChange('eotpremnica_enabled', value)}
                                  title={configFormData.eotpremnica_enabled ? t('enabled') : t('disabled')}
                                  disabled={eotpremnicaControlsDisabled}
                                />
                              </Form.Group>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('eotpremnica_url')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group>
                                <Form.Select
                                  value={String(configFormData.eotpremnica_url ?? '')}
                                  onChange={(e) => handleConfigInputChange('eotpremnica_url', e.target.value || null)}
                                  disabled={eotpremnicaControlsDisabled}
                                >
                                  <option value="">{t('select_option')}</option>
                                  <option value="https://api.demoeotpremnica.mfin.gov.rs">{t('eotpremnica_env_demo')}</option>
                                  <option value="https://api.eotpremnica.mfin.gov.rs">{t('eotpremnica_env_prod')}</option>
                                </Form.Select>
                              </Form.Group>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('eotpremnica_fetch_interval_seconds')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group>
                                <Form.Control
                                  type="number"
                                  min={1}
                                  value={String(configFormData.eotpremnica_fetch_interval_seconds ?? 120)}
                                  onChange={(e) =>
                                    handleConfigInputChange('eotpremnica_fetch_interval_seconds', e.target.value ? Number(e.target.value) : 120)
                                  }
                                  placeholder={t('enter_number')}
                                  disabled={eotpremnicaControlsDisabled}
                                />
                              </Form.Group>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('eotpremnica_max_document_age_days')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group>
                                <Form.Control
                                  type="number"
                                  min={1}
                                  value={String(configFormData.eotpremnica_max_document_age_days ?? '')}
                                  onChange={(e) =>
                                    handleConfigInputChange('eotpremnica_max_document_age_days', e.target.value ? Number(e.target.value) : null)
                                  }
                                  placeholder={t('enter_number')}
                                  disabled={eotpremnicaControlsDisabled}
                                />
                              </Form.Group>
                              <Form.Text className="text-muted">{t('eotpremnica_max_document_age_help')}</Form.Text>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('eotpremnica_api_key')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <Form.Group className="mb-2">
                                <Form.Control
                                  type="password"
                                  value={String(configFormData.eotpremnica_api_key || '')}
                                  onChange={(e) => handleConfigInputChange('eotpremnica_api_key', e.target.value)}
                                  placeholder={t('enter_text')}
                                  disabled={eotpremnicaControlsDisabled}
                                />
                              </Form.Group>
                              <Form.Text className="text-muted">
                                {configFormData.eotpremnica_api_key_set ? t('api_key_configured') : t('api_key_not_configured')}
                              </Form.Text>
                              <div className="d-flex">
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  className="w-100"
                                  disabled={eotpremnicaControlsDisabled || eotpremnicaTestLoading}
                                  onClick={async () => {
                                    if (!isEotpremnicaModuleEnabled) {
                                      return
                                    }

                                    const eotpremnicaUrl = configFormData.eotpremnica_url
                                    setEotpremnicaTestMessage(null)

                                    if (!eotpremnicaUrl) {
                                      setEotpremnicaTestVariant('danger')
                                      setEotpremnicaTestMessage(t('eotpremnica_url_required'))
                                      return
                                    }
                                    if (!configFormData.eotpremnica_api_key_set && !configFormData.eotpremnica_api_key) {
                                      setEotpremnicaTestVariant('danger')
                                      setEotpremnicaTestMessage(t('api_key_not_configured'))
                                      return
                                    }

                                    try {
                                      setEotpremnicaTestLoading(true)
                                      if (isConfigDirty()) {
                                        const saved = await handleConfigSave()
                                        if (!saved) {
                                          setEotpremnicaTestVariant('danger')
                                          setEotpremnicaTestMessage(t('error_saving_config'))
                                          return
                                        }
                                      }
                                      const result = await configService.testEotpremnicaConnection({
                                        eotpremnica_url: eotpremnicaUrl,
                                      })
                                      const message =
                                        result?.companies_count !== undefined && result?.companies_count !== null
                                          ? t('eotpremnica_connection_success_companies', { count: result.companies_count })
                                          : t('eotpremnica_connection_success')
                                      setEotpremnicaTestVariant('success')
                                      setEotpremnicaTestMessage(message)
                                    } catch (error: any) {
                                      const backendMessage = error?.response?.data?.message
                                      setEotpremnicaTestVariant('danger')
                                      setEotpremnicaTestMessage(backendMessage || t('eotpremnica_connection_failed'))
                                    } finally {
                                      setEotpremnicaTestLoading(false)
                                    }
                                  }}
                                >
                                  {eotpremnicaTestLoading ? t('testing_connection') : t('eotpremnica_test_connection')}
                                </Button>
                              </div>
                              {eotpremnicaTestMessage && (
                                <Alert variant={eotpremnicaTestVariant} className="mt-2 mb-0">
                                  {eotpremnicaTestMessage}
                                </Alert>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('eotpremnica_tags')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-n3">
                              <InputTags
                                value={configFormData.eotpremnica_tag_ids || []}
                                onChange={(value) => handleConfigInputChange('eotpremnica_tag_ids', value)}
                                disabled={eotpremnicaControlsDisabled}
                                menuPortalTarget={selectPortalTarget}
                              />
                              <Form.Text className="text-muted">{t('mail_rule_tags_help')}</Form.Text>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={12} md={12} lg={12}>
                        <Card className="mb-3">
                          <Card.Body>
                            <Card.Title as="div" className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-1 flex-grow-1">
                                <h6 className="mb-0">{t('eotpremnica_document_type_mapping')}</h6>
                              </div>
                            </Card.Title>
                            <div className="mb-2">
                              <Row className="g-4">
                                <Col xs={12} lg={6}>
                                  <div className="text-muted text-center fw-semibold fs-6 mb-2">{t('eotpremnica_despatch_title')}</div>
                                  <Table responsive borderless size="sm" className="mb-0" style={{ tableLayout: 'fixed' }}>
                                    <colgroup>
                                      <col style={{ width: '55%' }} />
                                      <col style={{ width: '45%' }} />
                                    </colgroup>
                                    <thead>
                                      <tr>
                                        <th className="align-middle">{t('eotpremnica_document_type_label')}</th>
                                        <th className="align-middle text-center">{t('eotpremnica_document_type')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {eotpremnicaDespatchMappings.map((mapping) => (
                                        <tr key={mapping.key}>
                                          <td className="align-middle">{t(mapping.labelKey)}</td>
                                          <td style={{ minWidth: 220 }} className="text-center align-middle">
                                            <div className="d-flex justify-content-center w-100">
                                              <div className="w-100">
                                                <InputSelect
                                                  value={(configFormData as any)[mapping.key] || null}
                                                  onChange={(value) => handleConfigInputChange(mapping.key, value || null)}
                                                  items={documentTypes}
                                                  bindLabel="name"
                                                  allowNull={true}
                                                  placeholder={t('select_option')}
                                                  disabled={eotpremnicaControlsDisabled}
                                                  menuPortalTarget={selectPortalTarget}
                                                />
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </Col>
                                <Col xs={12} lg={6}>
                                  <div className="text-muted text-center fw-semibold fs-6 mb-2">{t('eotpremnica_receipt_title')}</div>
                                  <Table responsive borderless size="sm" className="mb-0" style={{ tableLayout: 'fixed' }}>
                                    <colgroup>
                                      <col style={{ width: '55%' }} />
                                      <col style={{ width: '45%' }} />
                                    </colgroup>
                                    <thead>
                                      <tr>
                                        <th className="align-middle">{t('eotpremnica_document_type_label')}</th>
                                        <th className="align-middle text-center">{t('eotpremnica_document_type')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {eotpremnicaReceiptMappings.map((mapping) => (
                                        <tr key={mapping.key}>
                                          <td className="align-middle">{t(mapping.labelKey)}</td>
                                          <td style={{ minWidth: 220 }} className="text-center align-middle">
                                            <div className="d-flex justify-content-center w-100">
                                              <div className="w-100">
                                                <InputSelect
                                                  value={(configFormData as any)[mapping.key] || null}
                                                  onChange={(value) => handleConfigInputChange(mapping.key, value || null)}
                                                  items={documentTypes}
                                                  bindLabel="name"
                                                  allowNull={true}
                                                  placeholder={t('select_option')}
                                                  disabled={eotpremnicaControlsDisabled}
                                                  menuPortalTarget={selectPortalTarget}
                                                />
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </Col>
                              </Row>
                              <Form.Text className="text-muted">{t('mail_rule_document_type_help')}</Form.Text>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </div>
                <div className="d-flex gap-2 mb-3 mt-3">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={handleConfigDiscard}
                    disabled={eotpremnicaControlsDisabled || !isConfigDirty()}
                  >
                    {t('discard')}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!isEotpremnicaModuleEnabled || configSaving || !isConfigFormValid() || !isConfigDirty()}
                  >
                    {configSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('saving')}
                      </>
                    ) : (
                      t('save')
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </IfPermissions>
        )
      }

      case 'eSignature':
        return (
          <SettingsESignatureSection
            configLoading={configLoading}
            config={config}
            configFormData={configFormData}
            setConfigFormData={setConfigFormData}
            handleConfigInputChange={handleConfigInputChange}
            handleConfigSave={handleConfigSave}
            handleConfigDiscard={handleConfigDiscard}
            isConfigDirty={isConfigDirty}
            isConfigFormValid={isConfigFormValid}
            configSaving={configSaving}
            isElectronicSignatureModuleEnabled={isElectronicSignatureModuleEnabled}
          />
        )
      case 'active-directory':
        return <SettingsActiveDirectorySection />
      default:
        return null
    }
  }

  return (
    <div className="settings">
      <PageHeader title={t('settings')} info={t('settings_description')} infoLink="settings" />

      <div className="settings-container">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {menuItems.map((item) => {
              const isItemDisabled =
                (item.id === 'sef' && !isSefModuleEnabled) ||
                (item.id === 'eSignature' && !isElectronicSignatureModuleEnabled) ||
                (item.id === 'eotpremnica' && !isEotpremnicaModuleEnabled)

              const menuItem = (
                <button
                  key={item.id}
                  className={`settings-nav-item ${activeSection === item.id ? 'active' : ''} ${isItemDisabled ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!isItemDisabled) {
                      setActiveSection(item.id)
                    }
                  }}
                  disabled={isItemDisabled}
                  title={
                    isItemDisabled
                      ? t('settings_section_unavailable', {
                          defaultValue: 'This section is not available in your current package.',
                        })
                      : undefined
                  }
                >
                  <i className={`bi bi-${item.icon}`}></i>
                  <span>{item.label}</span>
                </button>
              )

              if (item.permission) {
                return (
                  <IfPermissions key={item.id} action={item.permission.action} type={item.permission.type}>
                    {menuItem}
                  </IfPermissions>
                )
              }

              return menuItem
            })}
          </nav>
        </div>

        <div className="settings-content">
          {renderContent()}

          {activeSection !== 'branding' &&
            activeSection !== 'ocr' &&
            activeSection !== 'barcode' &&
            activeSection !== 'eSignature' &&
            activeSection !== 'sef' &&
            activeSection !== 'eotpremnica' &&
            activeSection !== 'active-directory' && (
              <div className="d-flex gap-2 mb-3 mt-3 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={!isDirty && !((activeSection === 'documents' || activeSection === 'notifications') && isConfigDirty())}
                  onClick={handleReset}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={(!isDirty && !((activeSection === 'documents' || activeSection === 'notifications') && isConfigDirty())) || isSaving || configSaving}
                  onClick={handleSave}
                >
                  {isSaving || configSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {t('saving')}
                    </>
                  ) : (
                    t('save')
                  )}
                </button>
              </div>
            )}
        </div>
      </div>

      <DocumentationDialog show={showDocsDialog} onHide={() => setShowDocsDialog(false)} docKey={currentDocKey} />
    </div>
  )
}
